/** Console v0.5 (design brief v0.5, Prabash 23 Oct 2026): routes, the
 * sidebar's nav and who sees what. Pure -- no DOM -- so it's tested with
 * Node (web/console/test/v05-routes.test.ts). The server enforces every
 * rule; this only decides what's shown. */

export type SettingsSection = "profile" | "plan" | "notifications" | "data";

export type Route =
  | { kind: "home" }
  | { kind: "inbox" }
  | { kind: "organisations" }
  | { kind: "organisation"; id: string }
  | { kind: "team" }
  | { kind: "meetings" }
  | { kind: "meeting"; id: string }
  | { kind: "record"; client: string | null }
  | { kind: "agents" }
  | { kind: "reports" }
  | { kind: "connections" }
  | { kind: "settings"; section: SettingsSection }
  | { kind: "super-admin" }
  | { kind: "not-found" };

export type NavKey =
  | "home"
  | "inbox"
  | "organisations"
  | "team"
  | "meetings"
  | "agents"
  | "reports"
  | "connections"
  | "settings"
  | "super-admin";

export interface NavItem {
  key: NavKey;
  label: string;
  path: string;
}

/** Brief s.3: the sidebar's order. Super admin comes after a divider, for a super admin login only. */
export const NAV: readonly NavItem[] = [
  { key: "home", label: "Home", path: "/" },
  { key: "inbox", label: "Review inbox", path: "/inbox" },
  { key: "organisations", label: "Organisations", path: "/organisations" },
  { key: "team", label: "Team", path: "/team" },
  { key: "meetings", label: "Meetings", path: "/meetings" },
  { key: "agents", label: "Agents", path: "/agents" },
  { key: "reports", label: "Reports", path: "/reports" },
  { key: "connections", label: "Connections", path: "/connections" },
  { key: "settings", label: "Settings", path: "/settings" },
];

export const SUPER_ADMIN_NAV: NavItem = { key: "super-admin", label: "Super admin", path: "/super-admin" };

export const SETTINGS_SECTIONS: ReadonlyArray<{ section: SettingsSection; label: string; intro: string }> = [
  { section: "profile", label: "Profile and security", intro: "Your details, password, two-factor and devices." },
  { section: "plan", label: "Plan and billing", intro: "Your subscription, usage, card and invoices." },
  { section: "notifications", label: "Notifications", intro: "Choose what Preekie tells you about, and how." },
  { section: "data", label: "Data and privacy", intro: "Export your data or close the account." },
];

const ID = /^[A-Za-z0-9_-]{1,64}$/;
const MEETING_ID = /^[0-9a-f]{32}$/;
const SLUG = /^[a-z0-9-]{1,64}$/;

/** The address bar → a route. Paths from the current console that moved keep working. */
export function parseRoute(pathname: string, search = ""): Route {
  const parts = pathname.replace(/\/+$/, "").split("/").filter(Boolean);
  const [a, b, c] = parts;
  if (parts.length === 0) return { kind: "home" };
  if (parts.length === 1) {
    switch (a) {
      case "follow-up":
        return { kind: "home" };
      case "inbox":
        return { kind: "inbox" };
      case "organisations":
      case "clients":
        return { kind: "organisations" };
      case "team":
        return { kind: "team" };
      case "meetings":
        return { kind: "meetings" };
      case "agents":
        return { kind: "agents" };
      case "reports":
        return { kind: "reports" };
      case "connections":
        return { kind: "connections" };
      case "settings":
        return { kind: "settings", section: "profile" };
      case "super-admin":
        return { kind: "super-admin" };
    }
    return { kind: "not-found" };
  }
  if ((a === "organisations" || a === "clients") && b && ID.test(b) && (parts.length === 2 || (parts.length === 3 && c === "meetings"))) {
    return { kind: "organisation", id: b };
  }
  if (a === "meetings" && b === "record" && parts.length === 2) {
    const client = new URLSearchParams(search).get("client");
    return { kind: "record", client: client && SLUG.test(client) ? client : null };
  }
  if (a === "meetings" && b && MEETING_ID.test(b) && (parts.length === 2 || (parts.length === 3 && c === "summary"))) {
    return { kind: "meeting", id: b };
  }
  if (a === "settings" && parts.length === 2 && SETTINGS_SECTIONS.some((s) => s.section === b)) {
    return { kind: "settings", section: b as SettingsSection };
  }
  return { kind: "not-found" };
}

export function routePath(route: Route): string {
  switch (route.kind) {
    case "home":
      return "/";
    case "organisation":
      return `/organisations/${encodeURIComponent(route.id)}`;
    case "meeting":
      return `/meetings/${route.id}`;
    case "record":
      return route.client ? `/meetings/record?client=${encodeURIComponent(route.client)}` : "/meetings/record";
    case "settings":
      return route.section === "profile" ? "/settings" : `/settings/${route.section}`;
    case "not-found":
      return location.pathname;
    default:
      return `/${route.kind}`;
  }
}

/** Which sidebar item is current for a route. */
export function navKeyOf(route: Route): NavKey | null {
  switch (route.kind) {
    case "organisation":
      return "organisations";
    case "meeting":
    case "record":
      return "meetings";
    case "settings":
      return "settings";
    case "not-found":
      return null;
    default:
      return route.kind;
  }
}

/** The fields of /api/me the new console reads: session A's contract v1
 * (~/preekie-coord/proposals/A-console-contracts.md s.1). Absent = the current console. */
export interface AccountSummary {
  account_id: string;
  name: string;
  type: "practice" | "company";
  plan?: { preset: string; status: "pending_payment" | "active" | "suspended" } | null; // null: an account that predates plans
  limits?: { organisations: { used: number; max: number }; users: { used: number; max: number } } | null;
}

export interface V05Me {
  console_v05?: boolean;
  is_super_admin?: boolean;
  actor_type?: "member" | "service_team";
  display_name?: string | null;
  email?: string | null;
  account?: AccountSummary | null;
  capabilities?: string[];
  counts?: { awaiting_you: number; exceptions: number; requested: number; meetings_to_review?: number } | null;
  meetings_enabled?: boolean;
  xero_synced_at?: string | null; // A s.17: the latest Xero check held for organisations the person may see
  pinned_organisations?: Array<{ organisation_id: string; name: string }>; // A s.17: still in the person's scope
  accounts?: Array<{ account_id: string; name: string; type: "practice" | "company" }>; // the switcher's list
  entities?: Array<{ client_slug: string; client_name: string }>; // the organisations this person may see (as today)
  /** A s.21 / #431: pause_enabled is the server's Pause switch (cdk.json recorderPauseEnabled: test on, prod off until switched on). */
  recording_limits?: { limit_minutes: number; warn_minutes?: number; pause_enabled?: boolean };
}

/** A customer account that can't be used yet (awaiting payment) or any more (suspended): the server refuses every
 * customer route but /api/me and sign-out (A, contracts s.15), and the console shows one page saying which. */
export function accountGate(me: V05Me): "pending_payment" | "suspended" | null {
  if (me.is_super_admin === true) return null;
  const s = me.account?.plan?.status;
  return s === "pending_payment" || s === "suspended" ? s : null;
}

export function isAccountAdmin(me: V05Me): boolean {
  return me.is_super_admin !== true && me.actor_type !== "service_team" && (me.capabilities ?? []).includes("account_admin");
}

const CAPABILITY_LABELS: ReadonlyArray<[string, string]> = [
  ["account_admin", "Account admin"],
  ["approve", "Approver"],
  ["lodge", "Lodger"],
  ["review", "Reviewer"],
  ["prepare", "Preparer"],
];

/** The user chip's second line: "Account admin · Approver" (the two most senior), or "Preekie service team". */
export function roleLabel(me: V05Me): string | null {
  if (me.is_super_admin === true) return "Super admin";
  if (me.actor_type === "service_team") return "Preekie service team";
  const caps = new Set(me.capabilities ?? []);
  const labels = CAPABILITY_LABELS.filter(([c]) => caps.has(c)).map(([, l]) => l);
  return labels.length ? labels.slice(0, 2).join(" · ") : null;
}

/** The new console runs only when the server says so (console_v05 is set per environment and enforced by the server). */
export function useV05(me: V05Me | null, search: string): boolean {
  if (!me || me.console_v05 !== true) return false;
  // Test only: ?console=classic opens the current console while screens are rebuilt.
  return new URLSearchParams(search).get("console") !== "classic";
}

/** A super admin login sees only the super admin screen; a customer login never sees it (brief s.6). */
export function visibleRoute(route: Route, me: V05Me): Route {
  if (me.is_super_admin === true) return route.kind === "super-admin" || route.kind === "settings" ? route : { kind: "super-admin" };
  if (route.kind === "super-admin") return { kind: "not-found" };
  // Preekie's service team, under an account's grant: Home and the Review inbox only, read-only (A, contracts s.16).
  if (me.actor_type === "service_team" && route.kind !== "home" && route.kind !== "inbox") return { kind: "not-found" };
  if ((route.kind === "meetings" || route.kind === "meeting" || route.kind === "record") && me.meetings_enabled !== true) {
    return { kind: "not-found" };
  }
  return route;
}

export function navItems(me: V05Me): NavItem[] {
  if (me.is_super_admin === true) return [SUPER_ADMIN_NAV];
  if (me.actor_type === "service_team") return NAV.filter((n) => n.key === "home" || n.key === "inbox");
  return NAV.filter((n) => n.key !== "meetings" || me.meetings_enabled === true);
}

/** "Practice account · 24 organisations" / "Company account · 4 users" (brief s.5). */
export function accountLine(account: AccountSummary): string {
  const kind = account.type === "practice" ? "Practice account" : "Company account";
  const limits = account.limits;
  if (!limits) return kind;
  if (account.type === "practice") {
    const n = limits.organisations.used;
    return `${kind} · ${n} ${n === 1 ? "organisation" : "organisations"}`;
  }
  const n = limits.users.used;
  return `${kind} · ${n} ${n === 1 ? "user" : "users"}`;
}

/** "PG" from "Prabash G"; the email's first letter when there's no name. */
export function initials(name: string | null | undefined, email?: string | null): string {
  // Words that start with a letter or digit only, so "Name (synthetic)" gives "NN", not "N(".
  const words = (name ?? "").trim().split(/\s+/).filter((w) => /^[\p{L}\p{N}]/u.test(w));
  if (words.length >= 2) return (words[0]![0]! + words[words.length - 1]![0]!).toUpperCase();
  if (words.length === 1) return words[0]!.slice(0, 2).toUpperCase();
  return (email ?? "?").slice(0, 1).toUpperCase();
}

export function pageName(route: Route): string {
  switch (route.kind) {
    case "home":
      return "Home";
    case "inbox":
      return "Review inbox";
    case "organisations":
    case "organisation":
      return "Organisations";
    case "team":
      return "Team";
    case "meetings":
    case "meeting":
      return "Meetings";
    case "record":
      return "Record a meeting";
    case "agents":
      return "Agents";
    case "reports":
      return "Reports";
    case "connections":
      return "Connections";
    case "settings":
      return "Settings";
    case "super-admin":
      return "Super admin";
    case "not-found":
      return "Page not found";
  }
}

/** ABN as 00 000 000 000 (PRODUCT-UI.md s.5); anything that isn't 11 digits is shown as given. */
export function formatAbn(abn: string): string {
  const d = abn.replace(/\D/g, "");
  if (d.length !== 11) return abn;
  return `${d.slice(0, 2)} ${d.slice(2, 5)} ${d.slice(5, 8)} ${d.slice(8)}`;
}

/** The six display statuses (brief s.4, PRODUCT-UI.md s.3); coral is never one of them. */
export const STATUSES = ["Requested", "Prepared", "Awaiting review", "Approved", "Exception", "Lodged"] as const;
export type Status = (typeof STATUSES)[number];

export function statusClass(status: string): string {
  switch (status) {
    case "Prepared":
      return "pk5-chip-prepared";
    case "Awaiting review":
      return "pk5-chip-review";
    case "Approved":
      return "pk5-chip-approved";
    case "Exception":
      return "pk5-chip-exception";
    case "Lodged":
      return "pk5-chip-lodged";
    default:
      return "pk5-chip-requested";
  }
}

/** Roving focus for tabs (arrow keys, Home, End): the index to move to, or null. */
export function tabKeyTarget(key: string, current: number, count: number): number | null {
  if (count <= 0) return null;
  switch (key) {
    case "ArrowRight":
      return (current + 1) % count;
    case "ArrowLeft":
      return (current - 1 + count) % count;
    case "Home":
      return 0;
    case "End":
      return count - 1;
    default:
      return null;
  }
}

/** A link to a place on the current page (the skip link, "#pk5-content"): the id to focus, else null -- so the
 * router never treats it as a new screen (E: Skip to content + Enter didn't move focus). */
export function inPageTarget(href: string, here: { pathname: string; search: string }): string | null {
  let url: URL;
  try {
    url = new URL(href, "https://console.invalid" + here.pathname + here.search);
  } catch {
    return null;
  }
  if (!url.hash || url.hash.length < 2) return null;
  if (url.pathname !== here.pathname || url.search !== here.search) return null;
  try {
    return decodeURIComponent(url.hash.slice(1));
  } catch {
    return null; // a malformed % in the href (D): not a place on this page
  }
}

/** The Review inbox badge: awaiting you + exceptions, in the caller's own scope (A s.1, s.12). Missing or null counts
 * (an older server, or the items read failed) means no badge. */
export function openCount(me: Pick<V05Me, "counts">): number {
  return (me.counts?.awaiting_you ?? 0) + (me.counts?.exceptions ?? 0);
}
